/**
 * Drawer «Гайды»: руководство по веб-версии + гайды по DSL и методологии.
 *
 * Источники — те же markdown, что вендорятся в проекты пользователей
 * (templates/_shared/docs), заинлайнены на билде через Vite ?raw: единый
 * источник правды, ноль дрейфа между вебом и репо. Каждый гайд можно
 * скачать как .md (Blob-ссылка, без сервера).
 */

import { useState } from 'react';
import {
    Box, Button, Drawer, Group, ScrollArea, Tabs, Text,
} from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { MarkdownText } from './MarkdownText';

import webGuide from '../assets/web-guide.md?raw';
import dslGuide from '../../../../templates/_shared/docs/dsl-guide.md?raw';
import dslReference from '../../../../templates/_shared/docs/dsl-reference.md?raw';
import aspectsGuide from '../../../../templates/_shared/docs/aspects.md?raw';
import validationWorkbook from '../../../../templates/_shared/docs/validation-workbook.md?raw';
import conceptReview from '../../../../templates/_shared/docs/concept-review.md?raw';

interface GuideDef {
    key:      string;
    label:    string;
    file:     string;
    content:  string;
}

const GUIDES: GuideDef[] = [
    { key: 'web',        label: 'Веб-версия',       file: 'web-guide.md',           content: webGuide },
    { key: 'dsl',        label: 'Воркбук DSL',      file: 'dsl-guide.md',           content: dslGuide },
    { key: 'reference',  label: 'Глоссарий',        file: 'dsl-reference.md',       content: dslReference },
    { key: 'method',     label: 'Методология',      file: 'aspects.md',             content: aspectsGuide },
    { key: 'validation', label: 'Полевая проверка', file: 'validation-workbook.md', content: validationWorkbook },
    { key: 'review',     label: 'Разбор концепта',  file: 'concept-review.md',      content: conceptReview },
];

interface Props {
    opened:      boolean;
    onClose:     () => void;
    /** Стартовая вкладка ('web' | 'dsl' | …). */
    initialTab?: string;
}

export function GuidesDrawer({ opened, onClose, initialTab = 'web' }: Props): JSX.Element {
    const [tab, setTab] = useState(initialTab);
    const activeTab = GUIDES.some(g => g.key === tab) ? tab : 'web';
    const active = GUIDES.find(g => g.key === activeTab)!;

    const download = (): void => {
        const blob = new Blob([active.content], { type: 'text/markdown;charset=utf-8' });
        const url  = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = active.file;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            position="right"
            size="xl"
            title={<Text fw={600}>Гайды</Text>}
            styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', height: 'calc(100% - 60px)' } }}
        >
            <Tabs value={activeTab} onChange={v => setTab(v ?? 'web')} keepMounted={false} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <Tabs.List px="md">
                    {GUIDES.map(g => <Tabs.Tab key={g.key} value={g.key}>{g.label}</Tabs.Tab>)}
                </Tabs.List>
                <Group justify="flex-end" px="md" py={6} style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                    <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={download}>
                        Скачать {active.file}
                    </Button>
                </Group>
                <ScrollArea style={{ flex: 1 }} type="hover">
                    <Box px="lg" py="md">
                        <MarkdownText text={active.content} />
                    </Box>
                </ScrollArea>
            </Tabs>
        </Drawer>
    );
}
